// API client for the engineering flow (ingestion /api/v1/eng/*).
// Identity: a Google ID token (sign-in) is sent as a Bearer token; the workspace is chosen with X-EvalForge-Workspace.
// The server decides what the caller may see and do; nothing here is trusted for access control.
const BASE = (process.env.REACT_APP_INGESTION_URL || '') + '/api/v1/eng';
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const TOKEN_KEY = 'ef-idtoken', WS_KEY = 'ef-workspace';
const expiry = (t) => { try { return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp * 1000; } catch { return 0; } };
export const EXPIRED = 'Your sign-in expired. Sign in again to continue: your work is saved.';
export const session = {
  token() {
    const t = sessionStorage.getItem(TOKEN_KEY);
    return t && expiry(t) > Date.now() + 30000 ? t : null;
  },
  signedIn() { return !!sessionStorage.getItem(TOKEN_KEY); },        // true even when the token has just expired
  expiresAt() { const t = sessionStorage.getItem(TOKEN_KEY); return t ? expiry(t) : 0; },
  setToken(t) { if (t) sessionStorage.setItem(TOKEN_KEY, t); else sessionStorage.removeItem(TOKEN_KEY); },
  // signed in: remembered across visits; signed out: this tab session only, so a returning visitor sees the landing page
  workspace() { return (this.signedIn() ? localStorage.getItem(WS_KEY) : sessionStorage.getItem(WS_KEY)) || ''; },
  setWorkspace(w) {
    const store = this.signedIn() ? localStorage : sessionStorage;
    if (w) store.setItem(WS_KEY, w); else { localStorage.removeItem(WS_KEY); sessionStorage.removeItem(WS_KEY); }
  },
};

let onUnauthorized = () => {};
export const setOnUnauthorized = (fn) => { onUnauthorized = fn; };

function authHeaders() {
  const h = {}; const tok = session.token(), ws = session.workspace();
  if (tok) h.Authorization = `Bearer ${tok}`;
  if (ws) h['X-EvalForge-Workspace'] = ws;
  return h;
}

async function call(method, path, body) {
  if (session.signedIn() && !session.token()) {      // never fall back to an anonymous (demo) request when the sign-in expired
    onUnauthorized(EXPIRED);
    throw new Error(EXPIRED);
  }
  const headers = authHeaders();
  if (body) headers['Content-Type'] = 'application/json';
  const res = await fetch(BASE + path, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const text = await res.text();
  const data = text && res.headers.get('content-type')?.includes('json') ? JSON.parse(text) : text;
  if (res.status === 401) onUnauthorized(data && data.detail);
  if (!res.ok) throw new Error((data && data.detail) || `${method} ${path} failed: ${res.status}`);
  return data;
}

// Downloads need the auth headers too, so they go through fetch instead of a plain link.
export async function download(path, filename) {
  const res = await fetch(BASE + path, { headers: authHeaders() });
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  me:            ()                 => call('GET',  '/me'),
  demoStats:     ()                 => call('GET',  '/demo/stats'),
  internalDocs:  ()                 => call('GET',  '/internal-docs'),
  tryMsg:        (pid, body)        => call('POST', `/projects/${pid}/try`, body),
  suggest:       (pid, body)        => call('POST', `/projects/${pid}/suggest-cases`, body),
  probe:         (id)               => call('GET',  `/probes/${id}`),
  feedback:      (body)             => call('POST', '/feedback', body),
  labelAnswer:   (execId, label, note) => call('PUT', `/executions/${execId}/label`, { label, note: note || '' }),
  calibration:   (pid)              => call('GET', `/projects/${pid}/calibration`),
  review:        (pid)              => call('GET', `/projects/${pid}/review`),
  trends:        (pid)              => call('GET', `/projects/${pid}/trends`),
  tools:         (pid)              => call('GET', `/projects/${pid}/tools`),
  safetyPack:    (pid)              => call('GET', `/projects/${pid}/safety-pack`),
  search:        (pid, f)           => call('GET', `/projects/${pid}/search?${new URLSearchParams(Object.entries(f).filter(([, v]) => String(v).trim()))}`),
  createGroup:   (body)             => call('POST', '/change-groups', body),
  group:         (gid)              => call('GET', `/change-groups/${gid}`),
  myRatings:     (tid)              => call('GET',  `/feedback/mine?test_id=${tid}`),
  metrics:       (days)             => call('GET',  `/internal/metrics?days=${days || 30}`),
  event:         (event, props)     => call('POST', '/events', { event, props: props || {} }).catch(() => {}),
  internalDoc:   (slug)             => call('GET',  `/internal-docs/${slug}`),
  projects:      ()                 => call('GET',  '/projects'),
  project:       (pid)              => call('GET',  `/projects/${pid}`),
  saveProject:   (pid, body)        => call('PUT',  `/projects/${pid}`, body),
  setHeaders:    (pid, headers)     => call('PUT',  `/projects/${pid}/endpoint-headers`, { headers }),
  connectLangfuse:    (pid, body)   => call('PUT',  `/projects/${pid}/integrations/langfuse`, body),
  disconnectLangfuse: (pid)         => call('DELETE', `/projects/${pid}/integrations/langfuse`),
  langfuseTrace:      (eid)         => call('GET',  `/executions/${eid}/langfuse`),
  langfuseCoverage:   (tid)         => call('GET',  `/change-tests/${tid}/langfuse`),
  langfuseTraces:     (pid, only)   => call('GET',  `/projects/${pid}/langfuse/traces${only ? `?only=${only}` : ''}`),
  langfuseCase:       (pid, tid)    => call('GET',  `/projects/${pid}/langfuse/traces/${tid}/case`),
  deleteProject: (pid)              => call('DELETE', `/projects/${pid}`, { confirm: pid }),
  createVersion: (body)             => call('POST', '/versions', body),
  versionDiff:   (vid, against)     => call('GET',  `/versions/${vid}/diff${against ? `?against=${against}` : ''}`),
  createDataset: (pid, name)        => call('POST', `/projects/${pid}/datasets`, { name }),
  cases:         (did)              => call('GET',  `/datasets/${did}/cases`),
  addCase:       (did, c)           => call('POST', `/datasets/${did}/cases`, c),
  updateCase:    (iid, c)           => call('PUT',  `/cases/${iid}`, c),
  deleteCase:    (iid)              => call('DELETE', `/cases/${iid}`),
  createRun:     (vid, did, trials) => call('POST', '/runs', { version_id: vid, dataset_id: did, trials }),
  run:           (rid)              => call('GET',  `/runs/${rid}`),
  createTest:    (body)             => call('POST', '/change-tests', body),
  test:          (tid)              => call('GET',  `/change-tests/${tid}`),
  report:        (tid)              => call('GET',  `/change-tests/${tid}/report.md`),
  promote:       (tid, reason)      => call('POST', `/change-tests/${tid}/promote`, { override_reason: reason || '' }),
  analyze:       (tid, itemIds, runs) => call('POST', `/change-tests/${tid}/analyze`, { item_ids: itemIds || null, runs: runs || 3 }),
  applyTestFix:  (rcaId)            => call('POST', `/rca/${rcaId}/apply-test-fix`),
  keys:          ()                 => call('GET',  '/workspace/keys'),
  createKey:     (name)             => call('POST', '/workspace/keys', { name }),
  revokeKey:     (id)               => call('DELETE', `/workspace/keys/${id}`),
  settings:      ()                 => call('GET',  '/workspace/settings'),
  saveSettings:  (body)             => call('PUT',  '/workspace/settings', body),
};
export const exportPath = (tid, kind) => `/change-tests/${tid}/${kind}`;
